/**
 * useBodyScrollLock Hook
 * 
 * Manages body scroll lock for modals and overlays.
 * Replaces per-component body style mutations with a centralized solution.
 */

import { useEffect, useRef } from 'react';

interface UseBodyScrollLockOptions {
  /**
   * Target element to lock scroll for (defaults to document.body)
   */
  target?: HTMLElement;
  /**
   * Whether to preserve scroll position when locking
   */
  preserveScrollPosition?: boolean;
  /**
   * Whether to prevent layout shift by compensating for scrollbar width
   */
  preventLayoutShift?: boolean;
}

interface UseBodyScrollLockReturn {
  /**
   * Lock body scroll
   */
  lock: () => void;
  /**
   * Unlock body scroll
   */
  unlock: () => void;
  /**
   * Whether scroll is currently locked
   */
  isLocked: boolean;
}

export function useBodyScrollLock(
  options: UseBodyScrollLockOptions = {}
): UseBodyScrollLockReturn {
  const {
    target,
    preserveScrollPosition = true,
    preventLayoutShift = true,
  } = options;

  const isLockedRef = useRef(false);
  const scrollPositionRef = useRef(0);
  const originalStylesRef = useRef<{
    overflow: string;
    position: string;
    top: string;
    width: string;
    paddingRight: string;
  } | null>(null);

  const targetElement = target || (typeof document !== 'undefined' ? document.body : null);

  const lock = (): void => {
    if (!targetElement || isLockedRef.current) return;

    // Store current scroll position
    if (preserveScrollPosition) {
      scrollPositionRef.current = window.scrollY;
    }

    // Store original styles
    const computedStyle = window.getComputedStyle(targetElement);
    originalStylesRef.current = {
      overflow: computedStyle.overflow,
      position: computedStyle.position,
      top: computedStyle.top,
      width: computedStyle.width,
      paddingRight: computedStyle.paddingRight,
    };

    // Calculate scrollbar width to prevent layout shift
    const scrollbarWidth = preventLayoutShift 
      ? window.innerWidth - document.documentElement.clientWidth 
      : 0;

    // Apply lock styles
    targetElement.style.overflow = 'hidden';
    targetElement.style.position = 'fixed';
    targetElement.style.top = `-${scrollPositionRef.current}px`;
    targetElement.style.width = '100%';
    
    if (scrollbarWidth > 0) {
      targetElement.style.paddingRight = `${scrollbarWidth}px`;
    }

    isLockedRef.current = true;
  };

  const unlock = (): void => {
    if (!targetElement || !isLockedRef.current || !originalStylesRef.current) return;

    // Restore original styles
    const original = originalStylesRef.current;
    targetElement.style.overflow = original.overflow;
    targetElement.style.position = original.position;
    targetElement.style.top = original.top;
    targetElement.style.width = original.width;
    targetElement.style.paddingRight = original.paddingRight;

    // Restore scroll position
    if (preserveScrollPosition) {
      window.scrollTo(0, scrollPositionRef.current);
    }

    isLockedRef.current = false;
    originalStylesRef.current = null;
  };

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      if (isLockedRef.current) {
        unlock();
      }
    };
  }, []);

  return {
    lock,
    unlock,
    isLocked: isLockedRef.current,
  };
}

/**
 * Hook for managing body scroll lock with automatic cleanup
 */
export function useBodyScrollLockAuto(
  isLocked: boolean,
  options: UseBodyScrollLockOptions = {}
): void {
  const { lock, unlock } = useBodyScrollLock(options);

  useEffect(() => {
    if (isLocked) {
      lock();
    } else {
      unlock();
    }
  }, [isLocked, lock, unlock]);
}
